import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Loader2, Server } from "lucide-react";

interface FormData {
  name: string;
  description: string;
  url: string;
  auth_headers: string;
  enabled: boolean;
}

const emptyForm: FormData = {
  name: "",
  description: "",
  url: "",
  auth_headers: "{}",
  enabled: true,
};

const OutboundMcpServers = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [servers, setServers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchServers = async () => {
    if (!profile?.client_id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("outbound_mcp_servers")
      .select("*")
      .eq("client_id", profile.client_id)
      .order("created_at", { ascending: false });
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else setServers(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchServers(); }, [profile?.client_id]);

  const openAdd = () => { setEditingId(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (s: any) => {
    setEditingId(s.id);
    setForm({ name: s.name, description: s.description || "", url: s.url, auth_headers: JSON.stringify(s.auth_headers || {}, null, 2), enabled: s.enabled });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!profile?.client_id || !profile?.id) return;
    setSaving(true);
    let authHeaders: any = {};
    try { authHeaders = JSON.parse(form.auth_headers); } catch { toast({ title: "Invalid JSON in auth headers", variant: "destructive" }); setSaving(false); return; }

    if (editingId) {
      const { error } = await supabase.from("outbound_mcp_servers").update({ name: form.name, description: form.description, url: form.url, auth_headers: authHeaders, enabled: form.enabled }).eq("id", editingId);
      if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
      else { toast({ title: "Server updated" }); setDialogOpen(false); fetchServers(); }
    } else {
      const { error } = await supabase.from("outbound_mcp_servers").insert({ client_id: profile.client_id, user_id: profile.id, name: form.name, description: form.description, url: form.url, auth_headers: authHeaders, enabled: form.enabled });
      if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
      else { toast({ title: "Server added" }); setDialogOpen(false); fetchServers(); }
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from("outbound_mcp_servers").delete().eq("id", deleteId);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else { toast({ title: "Server deleted" }); fetchServers(); }
    setDeleteId(null);
  };

  const toggleEnabled = async (id: string, enabled: boolean) => {
    const { error } = await supabase.from("outbound_mcp_servers").update({ enabled }).eq("id", id);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else fetchServers();
  };

  if (loading) return <div className="flex items-center justify-center py-16 text-muted-foreground gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading...</div>;

  return (
    <div className="flex flex-1 flex-col overflow-auto">
      <div className="border-b border-border px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Outbound MCP Servers</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage external MCP servers your AI can call.</p>
          </div>
          <Button onClick={openAdd} size="sm"><Plus className="h-4 w-4 mr-1.5" />Add Server</Button>
        </div>
      </div>

      <div className="px-6 py-5">
        {servers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Server className="h-10 w-10 text-muted-foreground" />
            <p className="text-muted-foreground">No outbound MCP servers configured yet.</p>
            <Button onClick={openAdd} size="sm"><Plus className="h-4 w-4 mr-1.5" />Add your first server</Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {servers.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm truncate max-w-[200px]">{s.url}</TableCell>
                  <TableCell><Switch checked={s.enabled} onCheckedChange={(v) => toggleEnabled(s.id, v)} /></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(s)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(s.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit" : "Add"} MCP Server</DialogTitle>
            <DialogDescription>Configure an outbound MCP server connection.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="My MCP Server" /></div>
            <div className="grid gap-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" /></div>
            <div className="grid gap-1.5"><Label>URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://mcp.example.com" /></div>
            <div className="grid gap-1.5"><Label>Auth Headers (JSON)</Label><Textarea value={form.auth_headers} onChange={(e) => setForm({ ...form, auth_headers: e.target.value })} rows={3} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /><Label>Enabled</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.name.trim() || !form.url.trim() || saving}>{saving ? "Saving…" : editingId ? "Update" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete server?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OutboundMcpServers;
